// Owner: shell-nav (review fix round shell-home). Keystrokes between the moment the member asks for the
// command bar (⌘K, the rail's search button) and the moment its input has focus (the dialog mounts a
// frame later) are not lost: asking for the bar puts focus on a small off-screen field at once, so the
// browser itself collects what is typed (composition and paste included, no key listener), and the first
// input that takes focus inside the bar receives that text as if it had been typed there.
//
// The field stays in the page afterwards (hidden, not tabbable) as the place the dialog returns focus to
// when it closes: it hands focus on to the element that had it before the bar opened.

let field: HTMLInputElement | null = null;
let back: Element | null = null;
let armed = false;
let timer = 0;

const inBar = (el: EventTarget | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement && Boolean(el.closest('[data-testid="command-palette"]'));

/** Put text into a (possibly React-controlled) input and let its listeners know. */
function typeInto(input: HTMLInputElement, text: string): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, input.value + text);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function collector(): HTMLInputElement {
  if (field?.isConnected) return field;
  const el = document.createElement('input');
  el.type = 'text';
  el.setAttribute('aria-hidden', 'true');
  el.tabIndex = -1;
  el.autocomplete = 'off';
  el.spellcheck = false;
  el.setAttribute('data-dirty-ignore', '');
  // Off screen but focusable; 16px so a phone does not zoom into it.
  el.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;pointer-events:none';
  // Focus that lands here while nothing is being collected is only a return from the bar: pass it on.
  el.addEventListener('focus', () => {
    if (armed) return;
    const target = back;
    if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
    else el.blur();
  });
  document.body.appendChild(el);
  field = el;
  return el;
}

function disarm(): string {
  if (!armed) return '';
  armed = false;
  window.clearTimeout(timer);
  document.removeEventListener('focusin', onFocusIn, true);
  const text = field?.value ?? '';
  if (field) field.value = '';
  return text;
}

function onFocusIn(e: FocusEvent): void {
  if (!armed || !inBar(e.target)) return;
  const target = e.target;
  const text = disarm();
  if (text) typeInto(target, text);
}

/** Start keeping keystrokes (the command bar is about to open). Stops by itself once the bar has focus. */
export function armPaletteTypeahead(): void {
  if (armed || typeof document === 'undefined' || inBar(document.activeElement)) return;
  const el = collector();
  back = document.activeElement === el ? back : document.activeElement;
  armed = true;
  el.value = '';
  // If the bar never takes focus, nothing stays swallowed and focus goes back where it was.
  timer = window.setTimeout(() => {
    disarm();
    if (document.activeElement === el) {
      if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true });
      else el.blur();
    }
  }, 1_500);
  document.addEventListener('focusin', onFocusIn, true);
  el.focus({ preventScroll: true });
}

/** The element that had focus before the bar was asked for (the collecting field has it meanwhile). */
export function paletteOpener(): Element | null {
  return armed ? back : null;
}
